import type { ChangeEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { t } from '@/lib/i18n';
import { uploadAttachment } from '@/lib/uploadAttachment';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';

// Max file size for session uploads: 20 MB
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

interface UseAgentSessionUploadOptions {
  activeSessionId?: string | null;
}

export function useAgentSessionUpload({ activeSessionId }: UseAgentSessionUploadOptions) {
  const uploadRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadChipMsg, setUploadChipMsg] = useState<string | null>(null);

  // AC-M5-4: handle file selected from the hidden <input type="file">
  const handleFileUpload = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset input so same file can be re-selected
    if (uploadRef.current) uploadRef.current.value = '';
    if (!file || !activeSessionId) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadChipMsg(t('File too large (max 20 MB)'));
      setTimeout(() => setUploadChipMsg(null), 4000);
      return;
    }

    setIsUploading(true);
    setUploadChipMsg(`${t('Uploading')} ${file.name}…`);

    try {
      // Steps 1–3: presign → PUT → finalize (shared utility)
      const { attachmentId } = await uploadAttachment(file);

      // Step 4: link to session → triggers process-session-upload job
      await agentSessionsService.registerUpload(activeSessionId, attachmentId);

      setUploadChipMsg(`✓ ${file.name} ${t('uploaded')}`);
      setTimeout(() => setUploadChipMsg(null), 3000);
    } catch {
      setUploadChipMsg(t('Upload failed'));
      setTimeout(() => setUploadChipMsg(null), 4000);
    } finally {
      setIsUploading(false);
    }
  }, [activeSessionId]);

  return {
    uploadRef,
    isUploading,
    uploadChipMsg,
    handleFileUpload,
  };
}
